/**
 * Which LLM a session signs in on: the provider and the model id the gateway takes in the
 * handshake's `metadata`, as one selectable value — plus the choice of naming neither, which leaves
 * the backend on whatever model it defaults to.
 *
 * One list rather than two selects, because the two halves are not independent — the gateway
 * refuses a model whose provider was not named, and a provider's model ids mean nothing to the
 * other two. The catalog mirrors the eval harness's own list (andromeda-eval's Makefile), which is
 * what the numbers a tester compares against were produced with.
 */

export type Provider = 'bedrock' | 'gemini' | 'xai';

/** What the gateway is told: the two fields `signInRequest.metadata.llm` carries. */
export interface ModelSelection {
  provider: Provider;
  model: string;
}

/**
 * One row of the picker. Either a model this console names in the handshake, or the one choice that
 * names nothing — a union rather than a nullable provider, so no code path can read a provider off
 * the choice that has none.
 */
export type ModelChoice =
  | {
      kind: 'backendDefault';
      /** The value the settings and the `?model=` pin carry for "let the backend decide". */
      id: typeof DEFAULT_MODEL_ID;
      label: string;
    }
  | ({
      kind: 'model';
      /** `provider:model` — the single value the UI and the stored settings pass around. */
      id: string;
      label: string;
    } & ModelSelection);

/**
 * Splits the provider from the model id. A colon rather than a slash because no provider name
 * contains one, and Bedrock ids contain both a slash-free vendor prefix and a `:0` version
 * suffix — which is why only the *first* colon separates, and the rest belongs to the model.
 */
const SEPARATOR = ':';

export const PROVIDERS = [
  { id: 'bedrock', label: 'Bedrock' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'xai', label: 'xAI' },
] as const satisfies readonly { id: Provider; label: string }[];

/**
 * The models this console offers, per provider, in the order they are shown. Bedrock ids are the
 * cross-region inference profile form (`us.<vendor>.<model>`); Gemini and xAI ids are plain names
 * the gateway routes itself.
 *
 * Which providers are actually enabled is the target deployment's decision, not this list's:
 * naming a disabled one is refused at sign-in, and the refusal arrives as an error toast.
 */
const CATALOG = [
  { provider: 'bedrock', model: 'us.anthropic.claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { provider: 'bedrock', model: 'us.anthropic.claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  {
    provider: 'bedrock',
    model: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
    label: 'Claude Haiku 4.5',
  },
  {
    provider: 'bedrock',
    model: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0',
    label: 'Claude Sonnet 4.5',
  },
  {
    provider: 'bedrock',
    model: 'us.anthropic.claude-opus-4-5-20251101-v1:0',
    label: 'Claude Opus 4.5',
  },
  { provider: 'bedrock', model: 'us.amazon.nova-2-lite-v1:0', label: 'Nova 2 Lite' },
  { provider: 'bedrock', model: 'us.amazon.nova-pro-v1:0', label: 'Nova Pro' },
  { provider: 'bedrock', model: 'us.amazon.nova-lite-v1:0', label: 'Nova Lite' },
  { provider: 'bedrock', model: 'us.amazon.nova-micro-v1:0', label: 'Nova Micro' },
  { provider: 'gemini', model: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
  { provider: 'gemini', model: 'gemini-3.7-flash', label: 'Gemini 3.7 Flash' },
  { provider: 'gemini', model: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash' },
  { provider: 'gemini', model: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash' },
  { provider: 'gemini', model: 'gemini-3.5-flash-lite', label: 'Gemini 3.5 Flash Lite' },
  { provider: 'gemini', model: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite' },
  { provider: 'xai', model: 'grok-4.6', label: 'Grok 4.6' },
  { provider: 'xai', model: 'grok-4.5', label: 'Grok 4.5' },
  { provider: 'xai', model: 'grok-4.3', label: 'Grok 4.3' },
  { provider: 'xai', model: 'grok-4.20-0309-non-reasoning', label: 'Grok 4.20 (non-reasoning)' },
] as const satisfies readonly (ModelSelection & { label: string })[];

/** Joins a provider and a model id into the value the settings store and the `?model=` pin carry. */
export function formatModelId(provider: Provider, model: string): string {
  return `${provider}${SEPARATOR}${model}`;
}

/** A catalog entry, as opposed to the one choice that names no model. */
type ListedModel = Extract<ModelChoice, { kind: 'model' }>;

export const MODELS: readonly ListedModel[] = CATALOG.map((choice) => ({
  kind: 'model',
  ...choice,
  id: formatModelId(choice.provider, choice.model),
}));

/**
 * What a fresh install signs in on: no model at all, so the deployment answers on whatever it
 * defaults to. A deliberate starting point — the backend's own default is the configuration a tester
 * is comparing everything else against, and it is the only one no handshake can misname.
 */
export const DEFAULT_MODEL_ID = 'andromeda-default';

export const BACKEND_DEFAULT_CHOICE: ModelChoice = {
  kind: 'backendDefault',
  id: DEFAULT_MODEL_ID,
  label: 'Andromeda default',
};

/** Everything the picker offers: the backend's own default first, then the catalog by provider. */
const CHOICES: readonly ModelChoice[] = [BACKEND_DEFAULT_CHOICE, ...MODELS];

/** The heading the choice is listed under: its provider, or Andromeda for the backend's own pick. */
export function modelGroupLabel(choice: ModelChoice): string {
  return choice.kind === 'backendDefault' ? 'Andromeda' : providerLabel(choice.provider);
}

function isProvider(value: string): value is Provider {
  return PROVIDERS.some((provider) => provider.id === value);
}

/**
 * The provider's display name.
 *
 * @param provider the provider id
 * @returns its label, or the id itself for one this build does not list
 */
export function providerLabel(provider: string): string {
  return PROVIDERS.find((entry) => entry.id === provider)?.label ?? provider;
}

/**
 * Narrows a stored or link-pinned value to a provider and a model id.
 *
 * @param raw the `provider:model` value, from localStorage, a `?model=` query or a select
 * @returns the two halves, or null for anything that does not name a known provider and a model
 */
export function parseModelId(raw: string): ModelSelection | null {
  const separator = raw.indexOf(SEPARATOR);
  if (separator < 1) return null;

  const provider = raw.slice(0, separator).trim();
  const model = raw.slice(separator + 1).trim();
  if (!model || !isProvider(provider)) return null;

  return { provider, model };
}

/**
 * The short name for a choice's id: the catalog's label, the backend default's, or the bare model id
 * for one the catalog does not list — a `?model=` pin naming something newer than this build is shown
 * as what it is rather than silently as the default.
 *
 * @param id a `provider:model` value, or {@link DEFAULT_MODEL_ID}
 * @returns a label short enough for the top bar
 */
export function modelLabel(id: string): string {
  return modelChoice(id)?.label ?? id;
}

/**
 * The choice an id names: the backend default, a catalog entry, or one made up on the spot for a
 * model this build does not list, so a `?model=` pin is selectable and labelled rather than missing
 * from the list it should be in.
 *
 * @param id a `provider:model` value, or {@link DEFAULT_MODEL_ID}
 * @returns the choice, or null when the value names no known provider
 */
export function modelChoice(id: string): ModelChoice | null {
  if (id === DEFAULT_MODEL_ID) return BACKEND_DEFAULT_CHOICE;

  const known = MODELS.find((choice) => choice.id === id);
  if (known) return known;

  const selection = parseModelId(id);
  return selection === null ? null : { kind: 'model', ...selection, id, label: selection.model };
}

/**
 * Everything the picker offers, plus a model the catalog does not list — a `?model=` pin naming
 * something newer than this build — placed with its own provider rather than ahead of everything.
 *
 * @param extra the model to include, or null
 * @returns the offered choices, unchanged when the extra is already among them
 */
export function modelsWith(extra: ModelChoice | null): readonly ModelChoice[] {
  if (extra === null || extra.kind === 'backendDefault') return CHOICES;
  if (MODELS.some((choice) => choice.id === extra.id)) return CHOICES;

  return [
    BACKEND_DEFAULT_CHOICE,
    ...PROVIDERS.flatMap(({ id }) => {
      const group = MODELS.filter((choice) => choice.provider === id);
      return extra.provider === id ? [...group, extra] : group;
    }),
  ];
}

/**
 * The choices matching a typed query, in list order. Every whitespace-separated word has to appear
 * somewhere in the provider, the label or the model id, so "nova pro", "claude 4.5" and "anthropic"
 * all narrow the list the way the words were meant.
 *
 * @param query what the tester has typed, possibly empty
 * @param models the list to search, defaulting to everything offered
 * @returns the matching choices; all of them for an empty query
 */
export function searchModels(
  query: string,
  models: readonly ModelChoice[] = CHOICES,
): readonly ModelChoice[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return models;

  return models.filter((choice) => {
    const haystack = normalize(searchText(choice));
    return words.every((word) => haystack.includes(normalize(word)));
  });
}

/** What a choice is found by: its provider and id, or the words naming the backend's own default. */
function searchText(choice: ModelChoice): string {
  return choice.kind === 'backendDefault'
    ? `andromeda ${choice.label}`
    : `${choice.provider} ${providerLabel(choice.provider)} ${choice.label} ${choice.model}`;
}

/**
 * A label says "Claude Haiku 4.5" where the id says "claude-haiku-4-5": one spelling of the
 * separators, so typing either finds both.
 */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[-_.]/g, ' ');
}
